import { Card, CardBody } from "@progress/kendo-react-layout";
import { skipToken } from "@reduxjs/toolkit/query";
import { CheckCircle, AlertTriangle, XCircle } from "lucide-react";
import { SovraSkeleton } from "../../../../components/skeleton/sovraSkeleton";
import { useDataConfidenceRequest } from "../../../../hooks/useDataConfidenceRequest";
import { useGetDataConfidenceQuery } from "../../../../api/dataConfidenceApi";

type DataWarningTone = "warning" | "danger";

type DataWarning = {
    message: string;
    tone: DataWarningTone;
    icon: React.ReactNode;
};

const getWarningClassName = (tone: DataWarningTone): string => {
    switch (tone) {
        case "danger":
            return "border-danger-subtle bg-danger-subtle text-danger-emphasis";
        case "warning":
        default:
            return "border-warning-subtle bg-warning-subtle text-warning-emphasis";
    }
};

const toWarning = (message: string, index: number): DataWarning => ({
    message: message,
    tone: index === 0 ? "danger" : "warning",
    icon: index === 0 ? <XCircle className="k-text-icon-cross w-4 h-4" size={16} /> : <AlertTriangle className="k-text-icon-cross w-4 h-4" size={16} />
});

const renderLoadingRows = (count: number): React.ReactNode => (
    <div className="d-flex flex-column gap-2">
        {Array.from({ length: count }, (_, index) => (
            <div key={index} className="d-flex align-items-start gap-2">
                <SovraSkeleton shape="circle" style={{ width: 18, height: 18 }} className="flex-shrink-0 mt-1" />
                <SovraSkeleton shape="text" style={{ width: index === count - 1 ? "72%" : "92%", height: 18 }} />
            </div>
        ))}
    </div>
);

const DataQuality: React.FC = () => {
    const request = useDataConfidenceRequest();
    const { data, isError, isFetching, isLoading } = useGetDataConfidenceQuery(request);
    const isUnavailable = request === skipToken;
    const dataStrengths = data?.strengths ?? [];
    const dataWarnings = data?.warnings.map(toWarning) ?? [];
    const showLoading = isLoading || isFetching;

    return (
        <Card className="border border-light-subtle rounded-3 shadow-sm overflow-hidden min-w-0 w-100">
            <CardBody className="p-0">
                <div className="border-top px-3 px-lg-4 py-4">
                    <h3 className="h6 fw-bold mb-3">Data Strengths</h3>
                    {showLoading && renderLoadingRows(3)}
                    {!showLoading && isUnavailable && <p className="mb-0 text-muted">Data strengths are unavailable until a commercial plan is selected.</p>}
                    {!showLoading && !isUnavailable && isError && <p className="mb-0 text-danger">Unable to load data strengths.</p>}
                    {!showLoading && !isUnavailable && !isError && dataStrengths.length === 0 && (
                        <p className="mb-0 text-muted">No data strengths available.</p>
                    )}
                    {!showLoading && !isUnavailable && !isError && dataStrengths.length > 0 && (
                        <div className="d-flex flex-column gap-2">
                            {dataStrengths.map(strength => (
                                <div key={strength} className="d-flex align-items-center gap-2">
                                    <CheckCircle className="k-text-icon-check w-4 h-4" size={16} />
                                    <span>{strength}</span>
                                </div>
                            ))}
                        </div>
                    )}
                </div>

                <div className="border-top px-3 px-lg-4 py-4">
                    <h3 className="h6 fw-bold mb-3">Data Gaps / Warnings</h3>
                    {showLoading && renderLoadingRows(3)}
                    {!showLoading && isUnavailable && <p className="mb-0 text-muted">Data warnings are unavailable until a commercial plan is selected.</p>}
                    {!showLoading && !isUnavailable && isError && <p className="mb-0 text-danger">Unable to load data warnings.</p>}
                    {!showLoading && !isUnavailable && !isError && dataWarnings.length === 0 && (
                        <p className="mb-0 text-muted">No data warnings available.</p>
                    )}
                    {!showLoading && !isUnavailable && !isError && dataWarnings.length > 0 && (
                        <div className="d-flex flex-column gap-2">
                            {dataWarnings.map(warning => (
                                <div
                                    key={warning.message}
                                    className={`border rounded-3 px-3 py-2 d-flex align-items-center gap-2 ${getWarningClassName(warning.tone)}`}
                                >
                                    {warning.icon}
                                    <span>{warning.message}</span>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            </CardBody>
        </Card>
    );
};

export default DataQuality;
